import type {
  FrankieOneBcroContainer,
  FrankieOneKvp,
  FrankieOneKycCheck,
} from '@/integrations/Immersve/FrankieOne/types';
import type {
  CaseHit,
  CaseIdentityDetails,
  HitRecord,
  HitRecordKind,
  IdentityFieldDiff,
} from './caseDetail';
import { formatDate, fullName } from './caseDetail';

const LIST_KEYS = [
  ['checkResultsListSanctions', 'sanctions'],
  ['checkResultsListPEP', 'pep'],
  ['checkResultsListWatchlists', 'watchlist'],
  ['checkResultsListMedia', 'adverseMedia'],
] as const;

const HIT_STATES = new Set(['POSSIBLE_HIT', 'ACTIVE_HITS']);

function isHit(container: FrankieOneBcroContainer): boolean {
  return HIT_STATES.has(container.bcro?.currentState ?? '');
}

function kv(checkDetails: FrankieOneKvp[] | undefined, key: string): string | undefined {
  return checkDetails?.find((k) => k.kvpKey === key)?.kvpValue;
}

const ASSOCIATE_PREFIX = 'aml.search_entity_associate.';
function associates(checkDetails: FrankieOneKvp[] | undefined): string[] {
  return (checkDetails ?? [])
    .filter((k) => k.kvpKey?.startsWith(ASSOCIATE_PREFIX) && k.kvpValue)
    .map((k) => `${k.kvpValue} (${k.kvpKey!.slice(ASSOCIATE_PREFIX.length)})`);
}

function formatPossiblyPartialDate(raw: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? formatDate(raw) : raw;
}

function statusFor(
  matchTypes: string[],
  exactKeys: string[],
  partialKeys: string[]
): IdentityFieldDiff['status'] {
  if (matchTypes.some((t) => exactKeys.includes(t))) return 'match';
  if (matchTypes.some((t) => partialKeys.includes(t))) return 'partial';
  return 'unknown';
}

function buildGroupDiff(
  groupKv: FrankieOneKvp[] | undefined,
  identityDetails: CaseIdentityDetails
): IdentityFieldDiff[] {
  const matchTypes = (kv(groupKv, 'aml.search_match_types') ?? '').split(',').filter(Boolean);
  const matchedDob = kv(groupKv, 'aml.search_date_of_birth.matched');
  const rows: IdentityFieldDiff[] = [
    {
      field: 'Full name',
      subject: fullName(identityDetails.name),
      matched: kv(groupKv, 'aml.search_name.matched') ?? '—',
      status: statusFor(matchTypes, ['name_exact', 'equivalent_name'], ['name_fuzzy', 'aka_exact']),
    },
    {
      field: 'Date of birth',
      subject: formatPossiblyPartialDate(identityDetails.dateOfBirth),
      matched: matchedDob ? formatPossiblyPartialDate(matchedDob) : '—',
      status: statusFor(matchTypes, ['date_of_birth'], ['year_of_birth']),
    },
    {
      field: 'Address',
      subject: identityDetails.address,
      matched: '—',
      status: 'unknown',
    },
  ];

  const aka = kv(groupKv, 'aml.search_entity_aka');
  if (aka) rows.push({ field: 'AKA', subject: '—', matched: aka, status: 'unknown' });

  const nationality = kv(groupKv, 'aml.search_countries.matched');
  if (nationality) {
    rows.push({ field: 'Nationality', subject: '—', matched: nationality, status: 'unknown' });
  }

  const relatives = associates(groupKv);
  if (relatives.length) {
    rows.push({
      field: 'Associates',
      subject: '—',
      matched: relatives.join(', '),
      status: 'unknown',
    });
  }

  return rows;
}

const BACKGROUND_CHECK_TYPE_TO_KIND: Record<string, HitRecordKind> = {
  PEP: 'PEP_POSITION',
  SANCTION: 'SANCTION',
  WATCHLIST: 'SANCTION',
  MEDIA: 'ARTICLE',
};

const EXCLUDED_FIELD_KEYS = new Set([
  'SOURCE.name',
  'SOURCE.url',
  'SOURCE.country_codes',
  'MediaDetails',
  'checked_data_notes',
  'original_checksource',
  'requested_check_type',
]);
const EXCLUDED_FIELD_PREFIXES = ['aml.', 'status.', 'bcro.', 'original_raw_'];

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  'SOURCE.aml_types': 'Classification',
  Locationurl: 'Location URL',
  'Locationurl Internal': 'Location URL Internal',
};

function isDisplayableFieldKey(key: string): boolean {
  return !EXCLUDED_FIELD_KEYS.has(key) && !EXCLUDED_FIELD_PREFIXES.some((p) => key.startsWith(p));
}

function titleCaseWord(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function normalizeFieldLabel(key: string): string {
  if (FIELD_LABEL_OVERRIDES[key]) return FIELD_LABEL_OVERRIDES[key];
  const withoutSourcePrefix = key.replace(/^SOURCE\./, '');
  const label = /[._]/.test(withoutSourcePrefix)
    ? withoutSourcePrefix.split(/[._]+/).filter(Boolean).map(titleCaseWord).join(' ')
    : withoutSourcePrefix;
  return label.replace(/\b(url|id)\b/gi, (match) => match.toUpperCase());
}

function fieldsFor(checkDetails: FrankieOneKvp[] | undefined): { k: string; v: string }[] {
  const values = new Map<string, string[]>();
  for (const { kvpKey, kvpValue } of checkDetails ?? []) {
    if (!kvpKey || !kvpValue || !isDisplayableFieldKey(kvpKey)) continue;
    const label = normalizeFieldLabel(kvpKey);
    const existing = values.get(label) ?? [];
    if (!existing.includes(kvpValue)) existing.push(kvpValue);
    values.set(label, existing);
  }
  return [...values].map(([k, v]) => ({ k, v: v.join('\n') }));
}

interface MediaArticle {
  date?: string;
  title?: string;
  snippet?: string;
  url?: string;
}

function decodeMediaArticles(checkDetails: FrankieOneKvp[] | undefined): MediaArticle[] {
  const raw = kv(checkDetails, 'MediaDetails');
  if (!raw) return [];
  try {
    const decoded = JSON.parse(
      typeof atob === 'function' ? atob(raw) : Buffer.from(raw, 'base64').toString('utf-8')
    );
    return Array.isArray(decoded) ? decoded : [];
  } catch {
    return [];
  }
}

function containerToRecords(
  containerId: string | undefined,
  checkDetails: FrankieOneKvp[] | undefined,
  backgroundCheckType: string | undefined
): HitRecord[] {
  const id = containerId ?? '';
  if (backgroundCheckType === 'MEDIA') {
    return decodeMediaArticles(checkDetails).map((article, index) => ({
      id: `${id}_article_${index}`,
      kind: 'ARTICLE',
      title: article.title ?? 'Untitled article',
      meta: article.date ? article.date.slice(0, 10) : '',
      url: article.url,
      snippet: article.snippet,
    }));
  }

  const fields = fieldsFor(checkDetails);
  const sourceName = kv(checkDetails, 'SOURCE.name');
  const amlTypes = kv(checkDetails, 'SOURCE.aml_types');
  const country = kv(checkDetails, 'SOURCE.country_codes');
  const activeRange = [kv(checkDetails, 'Active Start Date'), kv(checkDetails, 'Active End Date')]
    .filter(Boolean)
    .join(' – ');

  return [
    {
      id,
      kind: BACKGROUND_CHECK_TYPE_TO_KIND[backgroundCheckType ?? ''] ?? 'SOURCE',
      title: sourceName ?? amlTypes ?? 'PEP list entry',
      meta: [country, activeRange].filter(Boolean).join(' · '),
      url: kv(checkDetails, 'SOURCE.url'),
      fields: fields.length ? fields : undefined,
    },
  ];
}

export function frankieOneToAmlHits(
  kycCheck: FrankieOneKycCheck,
  identityDetails: CaseIdentityDetails
): CaseHit[] {
  const amlResultSets = kycCheck.entityResult?.amlResultSets ?? [];

  return amlResultSets.flatMap((resultSet) => {
    const groupId = resultSet.groupDetails?.id;
    if (!groupId) return [];

    const groupKv = resultSet.groupDetails?.bcro?.checkDetails;
    const status = kv(groupKv, 'status.current');
    const hitTypes = LIST_KEYS.filter(([listKey]) => (resultSet[listKey] ?? []).some(isHit)).map(
      ([, hitType]) => hitType
    );

    const records = LIST_KEYS.flatMap(([listKey]) =>
      (resultSet[listKey] ?? []).flatMap((container) =>
        containerToRecords(
          container.id,
          container.bcro?.checkDetails,
          container.bcro?.backgroundCheckType
        )
      )
    );

    const hit: CaseHit = {
      id: groupId,
      matchedName: kv(groupKv, 'aml.search_name.matched') ?? kv(groupKv, 'aml.search_name') ?? '',
      verdict:
        status === 'true_positive'
          ? 'true_positive'
          : status === 'false_positive'
            ? 'false_positive'
            : 'unknown',
      hitTypes,
      diff: buildGroupDiff(groupKv, identityDetails),
      records,
    };
    return [hit];
  });
}
